import { Link } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight } from 'lucide-react';
import { api, STATUS_LABEL, type QuoteSummary } from '@/lib/api';
import { cn, formatMoney } from '@/lib/utils';

const STATUS_CHIP: Record<QuoteSummary['status'], string> = {
  DRAFT: 'bg-muted text-muted-foreground',
  SENT: 'bg-accent text-accent-foreground',
  ACCEPTED: 'bg-emerald-50 text-emerald-800',
  CANCELLED: 'bg-red-50 text-destructive',
};

/** Commandes récentes (les siennes pour un rep, toutes pour l'admin). */
export default function QuoteList({ custNum, title }: { custNum?: string; title: string }) {
  const list = useQuery({
    queryKey: ['quotes', custNum ?? null],
    queryFn: () =>
      api<{ quotes: QuoteSummary[] }>(`/api/quotes${custNum ? `?customer=${encodeURIComponent(custNum)}` : ''}`),
  });

  if (!list.data?.quotes.length) return null;

  return (
    <section className="panel overflow-hidden">
      <h2 className="panel-title border-b px-4 py-3">{title}</h2>
      <ul className="divide-y">
        {list.data.quotes.map((q) => (
          <li key={q.id}>
            <Link
              to={`/commandes/${q.id}`}
              className="group hover:bg-accent flex min-h-14 items-center gap-3 px-4 py-2 transition-colors"
            >
              <div className="min-w-0 flex-1">
                <div className="flex flex-wrap items-baseline gap-x-2">
                  <span className="text-sm font-bold tabular-nums">{q.number}</span>
                  {!custNum && <span className="truncate">{q.customerName}</span>}
                  {/* Liste d'un client : une commande passée sur un autre job le dit. */}
                  {custNum && q.custNum !== custNum && (
                    <span className="text-muted-foreground text-sm tabular-nums">
                      Job {q.job} · #{q.custNum}
                    </span>
                  )}
                </div>
                <div className="text-muted-foreground flex items-center gap-2 text-sm">
                  <span className={cn('rounded px-1.5 py-px text-xs font-medium', STATUS_CHIP[q.status])}>
                    {STATUS_LABEL[q.status]}
                  </span>
                  <span className="tabular-nums">
                    {q.lineCount} article{q.lineCount > 1 ? 's' : ''} ·{' '}
                    {new Date(q.updatedAt).toLocaleDateString('fr-CA')}
                  </span>
                </div>
              </div>
              <span className="font-bold tabular-nums">{formatMoney(q.total)}</span>
              <ChevronRight className="text-muted-foreground group-hover:text-primary size-5 shrink-0 transition-colors" />
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}
